<template>
  <fts-client-modal
    :footer="isFooter"
    append-content-class="workspace-modal__body"
    title="Workspace settings"
    class="fts-workspace-modal"
    ref="ftsClientModal"
  >
    <!-- header:icon -->
    <font-awesome-icon slot="icon" :icon="['fas', 'cog']" fixed-width />
    <!-- body -->
    <template #body>
      <div class="client-modal-spinner" v-if="loading">
        <fts-spinner show />
      </div>
      <div class="error-wrapper" v-else-if="isError">
        <div class="error-icon">
          <font-awesome-icon :icon="['fas', 'times']" />
        </div>
        <div class="error-title">An Error Occurred</div>
        <div class="error-description">
          {{ error }}
        </div>
      </div>
      <template v-else>
        <div class="user-profile-tabs">
          <div
            v-for="x in sections"
            :key="x.id"
            class="user-profile-tab"
            :class="{ active: section === x.id }"
            @click="section = x.id"
          >
            {{ x.name }}
          </div>
        </div>
        <!-- company section -->
        <div class="fts-row gutters" v-if="section === 'company'">
          <div class="fts-col-md-5 comfort-form-design">
            <div class="fts-col fts-form-group">
              <fts-image-cropper
                v-model="formData.companyLogo"
                :upload-form-data="{ workspaceId: workspaceId }"
                :cropper-options="{ aspectRatio: null }"
                :format-file-path="formatFilePath"
              />
            </div>
          </div>

          <div class="fts-col-md-7">
            <fts-form-element
              v-for="x in fields"
              :item="x"
              :form-data="formData"
              :validator="$v.formData"
              :key="x.id"
              comfort-design
            />
          </div>
        </div>
        <!-- subscription section: the billing API drives it (P2); the legacy static payment
             links only show when the Billing feature flag is off (summary 404 → fallback).
             Any OTHER failure (403 for a non-admin, a server error) must never show those
             links: they carry old prices and pay outside the webhook's attribution. -->
        <div v-if="section === 'subscription'">
          <fts-workspace-billing
            v-if="workspaceId && !billingFallback && !billingUnavailable"
            :workspace-id="workspaceId"
            @fallback="onBillingFallback"
          />

          <div class="workspace-modal-form-label plan-description" v-if="billingUnavailable === 'denied'">
            Only a workspace admin can view or change this workspace's plan and credits.
          </div>
          <div class="workspace-modal-form-label plan-description" v-else-if="billingUnavailable">
            Plan and credit information isn't available right now. Please try again in a moment.
          </div>

          <template v-if="billingFallback">
          <div class="workspace-modal-form-label plan-description">
            Please subscribe by clicking plan below. Unleash the power of
            <span class="fts-font-bold">Inistate</span> today!
          </div>

          <div class="plan-wrapper">
            <div class="plan-info">
              <div class="plan-name">Standard</div>
              <div class="plan-price">
                <div class="plan-price-tag">$828.00</div>
                <div class="plan-price-label">USD / year</div>
              </div>
            </div>
            <fts-btn
              tag="a"
              color="primary"
              box-shadow-none
              text-transform-init
              href="https://buy.stripe.com/7sI6qG6Okb586c0aEF"
              >Subscribe</fts-btn
            >
          </div>

          <div class="plan-wrapper">
            <div class="plan-info">
              <div class="plan-name">Standard</div>
              <div class="plan-price">
                <div class="plan-price-tag">$79.00</div>
                <div class="plan-price-label">USD / month</div>
              </div>
            </div>
            <fts-btn
              tag="a"
              color="primary"
              box-shadow-none
              text-transform-init
              href="https://buy.stripe.com/aEU6qG5Kgehk0RG9AC"
              >Subscribe</fts-btn
            >
          </div>
          </template>
        </div>
        <!-- workspace section -->
        <div class="workspace-section comfort-form-design" v-else-if="section === 'workspace'">
          <div class="workspace-modal-name-wrapper">
            <fts-form-element
              :item="workspaceFields[0]"
              :form-data="formData"
              :validator="$v.formData"
              comfortDesign
            >
              <div
                slot="errorMessage"
                v-if="$v.formData.name && $v.formData.name.format === false"
              >
                <div class="fts-text-default">
                  Allow alphabet and numeric only.
                </div>
                <div class="fts-text-default">
                  Allow special characters
                  <span
                    v-for="(x, ind) in workspaceNameSpecialCharacters"
                    :key="`wnsc-${ind}`"
                    class="fts-font-bold"
                    >{{ x }}</span
                  >
                </div>
                <div>Workspace name cannot contain invalid characters.</div>
              </div>
            </fts-form-element>
            <fts-contenteditable
              v-model="formData.description"
              placeholder="enter workspace description"
            />
          </div>

          <div class="setting-wrapper" v-if="isInternal">
            <div class="fts-form-group">
              <fts-form-label placement="top" icon="fts-icon-image">
                Logo <fts-release-badge alpha class="fts-align-top" />
              </fts-form-label>

              <div class="fts-form-helper">
                Replace the workspace name with the logo in the app.
              </div>

              <div class="setting-content fts-row gutters">
                <div class="fts-col fts-col-md-6">
                  <fts-image-cropper
                    v-model="formData.logo"
                    :upload-form-data="{ workspaceId: workspaceId }"
                    :cropper-options="{ aspectRatio: null }"
                    :format-file-path="formatFilePath"
                  />
                </div>
              </div>
            </div>

            <div class="fts-form-group">
              <fts-form-label placement="top" icon="fts-icon-image">
                Icon <fts-release-badge alpha class="fts-align-top" />
              </fts-form-label>

              <div class="fts-form-helper">
                Replace icon in the app.
              </div>

              <div class="setting-content fts-row gutters">
                <div class="fts-col fts-col-md-6">
                  <fts-image-cropper
                    v-model="formData.icon"
                    :upload-form-data="{ workspaceId: workspaceId }"
                    :format-file-path="formatFilePath"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="setting-wrapper" v-if="isInternal">
            <fts-form-label placement="top" icon="fts-icon-cog">
              Preferences <fts-release-badge alpha class="fts-align-top" />
            </fts-form-label>
            
            <div class="fts-form-helper">
              Customize various options to enhance and personalize your user experience.
            </div>

            <div class="setting-content fts-row gutters">
              <div class="fts-col fts-col-md-6 fts-form-group preferences-color-form-group">
                <fts-form-label placement="top" icon="fts-icon-code">
                  Primary color
                </fts-form-label>

                <div class="preferences-color-control">
                  <input type="text" class="fts-form-control" v-model="formData.color1" />
                  <input type="color" v-model="formData.color1" class="preferences-color-toggle" />
                </div>
              </div>

              <div class="fts-col fts-col-md-6 fts-form-group preferences-color-form-group">
                <fts-form-label placement="top" icon="fts-icon-code">
                  Secondary color
                </fts-form-label>

                <div class="preferences-color-control">
                  <input type="text" class="fts-form-control" v-model="formData.color2" />
                  <input type="color" v-model="formData.color2" class="preferences-color-toggle" />
                </div>
              </div>
            </div>
          </div>

          <div class="setting-wrapper" v-if="isInternal">
            <fts-collapsible-element class="fts-form-group collapsible-element">
              <div
                slot="toggle"
                slot-scope="{ toggle, isVisible }"
                class="collapsible-element-toggle"
                :class="{'collapsible-element-toggle--expand': isVisible}"
                @click="toggle"
              >
                <div class="collapsible-element-toggle-header">
                  <fts-form-label icon="fts-icon-envelope">
                    Email <fts-release-badge alpha class="fts-align-top" />
                  </fts-form-label>
                  <div class="fts-form-helper">
                    Customize sender email settings including SMTP server, port number, SSL encryption, and etc.
                  </div>
                </div>

                <div class="collapsible-element-toggle-action">
                  <font-awesome-icon :icon="['fas', 'caret-right']" />
                </div>
              </div>

              <div slot="content" class="collapsible-element-content">
                <fts-form-element
                  v-for="x in emailFields"
                  :key="x.id"
                  :item="x"
                  :form-data="formData"
                  :validator="$v.formData"
                  comfort-design
                />
              </div>
            </fts-collapsible-element>
          </div>

          <div class="setting-wrapper" v-if="isInternal">
            <fts-collapsible-element class="fts-form-group collapsible-element">
              <div
                slot="toggle"
                slot-scope="{ toggle, isVisible }"
                class="collapsible-element-toggle"
                :class="{'collapsible-element-toggle--expand': isVisible}"
                @click="toggle"
              >
                <div class="collapsible-element-toggle-header">
                  <fts-form-label icon="fts-icon-envelope">
                    Whatsapp <fts-release-badge alpha class="fts-align-top" />
                  </fts-form-label>
                  <div class="fts-form-helper">
                    Customize Whatsapp sender.
                  </div>
                </div>

                <div class="collapsible-element-toggle-action">
                  <font-awesome-icon :icon="['fas', 'caret-right']" />
                </div>
              </div>

              <div slot="content" class="collapsible-element-content">
                <fts-form-element
                  v-for="x in whatsappFields"
                  :key="x.id"
                  :item="x"
                  :form-data="formData"
                  :validator="$v.formData"
                  comfort-design
                />
              </div>
            </fts-collapsible-element>
          </div>

          <div class="setting-wrapper" v-if="isInternal">
            <div class="fts-form-group">
              <fts-form-label placement="top" icon="fts-icon-check-square">
                Beta Mode <fts-release-badge alpha class="fts-align-top" />
              </fts-form-label>

              <div class="fts-form-helper">
                Enable to access beta features.
              </div>

              <fts-toggle-switch
                v-model="formData.beta"
                color="primary"
                mat-class
                :size="16"
              />
            </div>
          </div>

          <div class="setting-wrapper" v-if="isInternal">
            <div class="fts-form-group">
              <fts-form-label placement="top" icon="fts-icon-duplicate">
                Clone workspace <fts-release-badge alpha class="fts-align-top" />
              </fts-form-label>

              <div class="fts-form-helper">
                Clone this workspace to create a new workspace.
              </div>

              <fts-btn
                color="secondary"
                outline
                text-transform-init
                :disabled="submitting"
                @click="cloneWorkspace"
              >
                Clone this workspace configuration
              </fts-btn>
            </div>
          </div>

          <div class="setting-wrapper">
            <div class="fts-form-group">
              <fts-form-label placement="top" icon="fts-icon-exclamation-triangle">
                Danger zone
              </fts-form-label>

              <div class="fts-form-helper">
                Once you delete a workspace, there is no going back. Ensure you are certain.
              </div>

              <fts-btn
                color="danger"
                outline
                text-transform-init
                :disabled="submitting"
                @click="deleteWorkspace"
              >
                Delete this workspace
              </fts-btn>
            </div>
          </div>
        </div>
        <!-- app section -->
        <div class="app-section" v-else-if="section === 'app'">
          <div class="workspace-modal-form-label">
            Just go to Apple App Store or Google Play Store to download and
            install.
          </div>
          <div class="workspace-modal-form-label">
            Search for <span class="fts-font-bold">Inistate</span>, install and
            login with your account.
          </div>

          <div>
            <a class="app-store-btn" :href="iOSAppStoreURL" target="_blank">
              <img src="../../assets/images/brand/app-store-black.svg" />
            </a>
            <a
              class="app-store-btn"
              :href="androindPlayStoreURL"
              target="_blank"
            >
              <img src="../../assets/images/brand/google-play-badge.svg" />
            </a>
          </div>

          <div class="workspace-modal-form-label">
            Need more help?
            <a :href="communityURL" target="_blank" class="fts-link-primary"
              >Click here</a
            >
          </div>
        </div>
        <!-- AI teammate section: workspace-wide standing instructions appended to every
             GNey system prompt (chat, actions, automation). Saved via its own targeted
             endpoint — independent of the footer Update; propagates within a minute. -->
        <div class="teammate-section comfort-form-design" v-if="section === 'teammate'">
          <div class="client-modal-spinner" v-if="teammate.loading">
            <fts-spinner show />
          </div>
          <template v-else>
            <div class="fts-form-group">
              <fts-form-label placement="top" icon="fts-icon-magic-lamp">
                Workspace instructions
              </fts-form-label>
              <div class="workspace-modal-form-label">
                Standing instructions GNey follows in every conversation and automation in
                this workspace — language, terminology, defaults, and procedure. They cannot
                override platform rules (approvals, permissions, confirmations). For
                module-specific behavior, use the module designer's AI annotations instead.
              </div>
              <textarea
                v-model="teammate.instructions"
                class="teammate-instructions-input"
                :maxlength="teammate.maxLength"
                rows="10"
                placeholder="e.g. Always answer in Malay. Customers are called 'members'. Amounts are in MYR. Flag anything above RM10,000 to the Finance team before proposing it."
              />
              <div class="teammate-instructions-meta">
                <span>Takes effect within a minute.</span>
                <span>{{ (teammate.instructions || "").length }} / {{ teammate.maxLength }}</span>
              </div>
            </div>
            <fts-btn
              color="primary"
              box-shadow-none
              :loading="teammate.saving"
              :disabled="teammate.instructions === teammate.original"
              @click="saveTeammateInstructions"
            >
              <font-awesome-icon :icon="['fas', 'check']" />Save instructions
            </fts-btn>
          </template>
        </div>
        <!-- Access section (SS03547): who may use the "everyone" views. Both restrictions are off
             by default; when on, only Administrators, Workspace Admins and Consultants keep the
             option and the server refuses it for everyone else. Saved via its own targeted
             endpoint — independent of the footer Update. -->
        <div class="access-section comfort-form-design" v-if="section === 'access'">
          <div class="client-modal-spinner" v-if="access.loading">
            <fts-spinner show />
          </div>
          <template v-else>
            <div class="fts-form-group">
              <fts-form-label placement="top" icon="fts-icon-cog">
                Everyone views
              </fts-form-label>
              <div class="workspace-modal-form-label">
                By default any member can switch the dashboard activity stream to
                <span class="fts-font-bold">everyone</span> and add a listing calendar
                <span class="fts-font-bold">for everyone</span>. Turn a restriction on to keep
                that option for workspace admins only; other members then see their own activity
                and add calendars for themselves.
              </div>
              <div class="access-option">
                <fts-toggle-switch
                  v-model="access.restrictActivityStream"
                  color="primary"
                  mat-class
                  :size="16"
                  :label="false"
                />
                <div class="access-option-label" @click="access.restrictActivityStream = !access.restrictActivityStream">
                  Only admins can view everyone's activity stream
                </div>
              </div>
              <div class="access-option">
                <fts-toggle-switch
                  v-model="access.restrictSharedCalendar"
                  color="primary"
                  mat-class
                  :size="16"
                  :label="false"
                />
                <div class="access-option-label" @click="access.restrictSharedCalendar = !access.restrictSharedCalendar">
                  Only admins can add a calendar for everyone
                </div>
              </div>
            </div>
            <fts-btn
              color="primary"
              box-shadow-none
              :loading="access.saving"
              :disabled="!accessChanged"
              @click="saveEveryoneAccess"
            >
              <font-awesome-icon :icon="['fas', 'check']" />Save access
            </fts-btn>
          </template>
        </div>
      </template>
      <!-- feedback indicate -->
      <fts-feedback-indicate modal ref="ftsFeedbackIndicate" />
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <fts-btn text :disabled="submitting" @click="beforeClose">
        <font-awesome-icon :icon="['fas', 'times']" />Close
      </fts-btn>

      <fts-btn color="primary" :loading="submitting" @click="submit">
        <font-awesome-icon :icon="['fas', 'check']" />Update
      </fts-btn>
    </template>
  </fts-client-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import changesets from "diff-json";

import webAppConfig from "../commons/webApp.config";
import commons from "../commons/commons";
import designValidator from "../commons/designValidator";
import { authorizationStore } from "../store/authorizationStore";
import { store as canvasDesignStore } from "../store/canvasDesignStore";

import countriesList from "../../json/countries.json";

import FTSClientModal from "../elements/FTSClientModal";
import FTSBtn from "../elements/FTSBtn";
import FTSFormLabel from "../elements/FTSFormLabel";
import FTSFormElement from "../elements/FTSFormElement";
import FTSFeedbackIndicate from "../elements/FTSFeedbackIndicate";
import FTSSpinner from "../elements/FTSSpinner";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSToggleSwitch from '../elements/FTSToggleSwitch';
import FTSImageCropper from '../elements/FTSImageCropper';
import FTSWorkspaceBilling from './FTSWorkspaceBilling';

export default {
  components: {
    "fts-client-modal": FTSClientModal,
    "fts-btn": FTSBtn,
    "fts-form-label": FTSFormLabel,
    "fts-form-element": FTSFormElement,
    "fts-feedback-indicate": FTSFeedbackIndicate,
    "fts-spinner": FTSSpinner,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-contenteditable": FTSContenteditable,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-image-cropper": FTSImageCropper,
    "fts-workspace-billing": FTSWorkspaceBilling
  },
  data: () => ({
    workspaceId: null,
    iOSAppStoreURL: commons.iOSAppStoreURL,
    androindPlayStoreURL: commons.androindPlayStoreURL,
    communityURL: commons.communityURL,
    sections: [
      { id: "workspace", name: "Workspace" },
      { id: "company", name: "Company" },
      { id: "app", name: "App" },
      { id: "subscription", name: "Plan & Credits" },
      { id: "teammate", name: "AI Teammate" },
      { id: "access", name: "Access" },
    ],
    section: "workspace",
    billingFallback: false,
    // "denied" (403) | "error" (anything else but 404) | null — see onBillingFallback.
    billingUnavailable: null,
    /* Workspace-wide GNey instructions: lazily loaded on first tab open, saved via its
       own targeted endpoint — deliberately outside formData so the footer Update and the
       discard-changes diff never touch it. */
    teammate: {
      instructions: "",
      original: "",
      maxLength: 4000,
      loaded: false,
      loading: false,
      saving: false
    },
    /* SS03547: workspace "everyone" access policy - same lazy-load / own-endpoint shape as
       teammate, and outside formData for the same reasons. */
    access: {
      restrictActivityStream: false,
      restrictSharedCalendar: false,
      original: null,
      loaded: false,
      loading: false,
      saving: false
    },
    formData: {},
    originalData: null,
    resolve: null,
    loading: false,
    submitting: false,
    isError: false,
    error: null
  }),
  validations() {
    let formData = {};
    if (this.section === "workspace")
      formData = commons.getFormValidators(this.workspaceFields);
    else if (this.section === "company")
      formData = commons.getFormValidators(this.fields);

    return { formData };
  },
  watch: {
    section(value) {
      // Lazy-load: the teammate tab's content is fetched on first open only.
      if (value === "teammate") this.loadTeammateInstructions();
      if (value === "access") this.loadEveryoneAccess();
    },
  },
  methods: {
    async loadTeammateInstructions() {
      if (this.teammate.loaded || this.teammate.loading) return;
      this.teammate.loading = true;
      try {
        const res = await canvasDesignStore.dispatch(
          "retrieveTeammateInstructions",
          this.workspaceId
        );
        this.teammate.instructions = (res.data && res.data.instructions) || "";
        if (res.data && res.data.maxLength) this.teammate.maxLength = res.data.maxLength;
        this.teammate.original = this.teammate.instructions;
        this.teammate.loaded = true;
      } catch (err) {
        this.$refs.ftsFeedbackIndicate.show({
          type: "error",
          response: err.response,
        });
      } finally {
        this.teammate.loading = false;
      }
    },
    async saveTeammateInstructions() {
      this.teammate.saving = true;
      try {
        const res = await canvasDesignStore.dispatch("saveTeammateInstructions", {
          workspaceId: this.workspaceId,
          instructions: this.teammate.instructions,
        });
        // The server echoes the sanitized text (control chars stripped, trimmed).
        this.teammate.instructions = (res.data && res.data.instructions) || "";
        this.teammate.original = this.teammate.instructions;
        this.$toasted.success("Teammate instructions saved");
      } catch (err) {
        this.$refs.ftsFeedbackIndicate.show({
          type: "error",
          response: err.response,
        });
      } finally {
        this.teammate.saving = false;
      }
    },
    /* SS03547 */
    async loadEveryoneAccess() {
      if (this.access.loaded || this.access.loading) return;
      this.access.loading = true;
      try {
        const res = await canvasDesignStore.dispatch("retrieveEveryoneAccess", this.workspaceId);
        this.applyEveryoneAccess(res.data);
        this.access.loaded = true;
      } catch (err) {
        this.$refs.ftsFeedbackIndicate.show({
          type: "error",
          response: err.response,
        });
      } finally {
        this.access.loading = false;
      }
    },
    applyEveryoneAccess(data) {
      this.access.restrictActivityStream = Boolean(data && data.restrictActivityStream);
      this.access.restrictSharedCalendar = Boolean(data && data.restrictSharedCalendar);
      this.access.original = {
        restrictActivityStream: this.access.restrictActivityStream,
        restrictSharedCalendar: this.access.restrictSharedCalendar,
      };
    },
    async saveEveryoneAccess() {
      this.access.saving = true;
      try {
        const res = await canvasDesignStore.dispatch("saveEveryoneAccess", {
          workspaceId: this.workspaceId,
          restrictActivityStream: this.access.restrictActivityStream,
          restrictSharedCalendar: this.access.restrictSharedCalendar,
        });
        this.applyEveryoneAccess(res.data);
        this.$toasted.success("Access settings saved");
      } catch (err) {
        this.$refs.ftsFeedbackIndicate.show({
          type: "error",
          response: err.response,
        });
      } finally {
        this.access.saving = false;
      }
    },
    onBillingFallback(status) {
      // 404 is the feature flag being off — the one case the legacy links are meant for.
      if (status === 404) {
        this.billingFallback = true;
      } else {
        this.billingUnavailable = status === 403 ? "denied" : "error";
      }
    },
    async show(item) {
      if (item && item.id) {
        this.workspaceId = item.id;
        // A previous workspace's verdict must not leak into this one.
        this.billingFallback = false;
        this.billingUnavailable = null;
      } else {
        this.$toasted.global.error();
        return;
      }
      /* section */
      if (item.section) {
        const findIndex = this.sections.findIndex((x) => x.id === item.section);
        if (findIndex > -1) {
          this.section = item.section;
        }
      }

      return new Promise(async (resolve) => {
        this.loading = true;
        this.isError = false;
        this.resolve = resolve;
        this.$refs.ftsClientModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });
            } else {
              this.reset();
            }
          },
        });

        try {
          const res = await canvasDesignStore.dispatch(
            "retrieveWorkspaceInformation",
            this.workspaceId
          );
          this.formData = res.data;
        } catch (err) {
          this.error =
            (err.response && err.response.data && err.response.data.message) ||
            "Something went wrong.";
          this.isError = true;
        } finally {
          this.loading = false;
          this.$nextTick(() => {
            this.originalData = _.cloneDeep(this.formData);
          });
        }
      });
    },
    beforeClose() {
      this.$refs.ftsClientModal.beforeClose();
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsClientModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    async cloneWorkspace() {
      try {
        this.submitting = true;
        const formData = { id: this.workspaceId.toString() };
        let res = await canvasDesignStore.dispatch("cloneWorkspace", formData);
        this.$toasted.success("Workspace cloned successfully");
        this.resolve && this.resolve(res.data);
        this.reset();
      } catch (err) {
        this.$refs.ftsFeedbackIndicate.show({
          type: "error",
          response: err.response,
        });
      } finally {
        this.submitting = false;
      }
    },
    async deleteWorkspace() {
      const workspaceName = (this.formData.name || this.originalData.name || "Workspace").trim();
      const res = await this.$confirmation.show({
        type: "delete",
        title: "Workspace",
        text: workspaceName,
        enableConfirmationKey: true,
        confirmationKey: workspaceName
      });

      if (res === true) {
        try {
          this.submitting = true;
          const formData = { id: this.workspaceId.toString() };
          let res2 = await canvasDesignStore.dispatch("deleteWorkspace", formData);

          this.$toasted.success("Workspace deleted successfully");
          this.resolve && this.resolve({ id: this.workspaceId, type: "delete" });
          this.reset();
        } catch(err) {
          this.$refs.ftsFeedbackIndicate.show({
            type: "error",
            response: err.response,
          });
        } finally {
          this.submitting = false;
        }
      }
    },
    async submit() {
      this.submitting = true;
      this.$v.formData.$touch();
      if (this.$v.formData.$invalid) {
        this.$toasted.global.invalidFormValidate();
      } else {
        try {
          const formData = { ...this.formData, id: this.workspaceId };
          await canvasDesignStore.dispatch(
            "updateWorkspaceInformation",
            formData
          );
          this.$toasted.success(`Workspace is updated successfully`);
          this.resolve && this.resolve(formData);
          this.reset();
        } catch (err) {
          this.$refs.ftsFeedbackIndicate.show({
            type: "error",
            response: err.response,
          });
        }
      }
      this.submitting = false;
    },
    formatFilePath(filePath) {
      if (filePath) {
        const url = filePath.split('/');
        const filename = encodeURIComponent(url.pop());
        const formattedUrl = `${url.join("/")}/${filename}`;
        
        if (formattedUrl.toLowerCase().startsWith('/uploads')) {
          return `${webAppConfig.baseURL}/${formattedUrl}`;
        } else {
          const encodeURL = `/api/d/${this.workspaceId}/s${formattedUrl}`;
          return webAppConfig.baseURL
            ? `${webAppConfig.baseURL}${encodeURL}`
            : encodeURL;
        }
      } else {
        return commons.defaultImage;
      }
    }
  },
  computed: {
    /* SS03547: the Access tab's Save is live only once a toggle differs from what was loaded. */
    accessChanged() {
      const original = this.access.original;
      return Boolean(original)
        && (this.access.restrictActivityStream !== original.restrictActivityStream
          || this.access.restrictSharedCalendar !== original.restrictSharedCalendar);
    },
    workspaceNameSpecialCharacters() {
      return designValidator.workspaceNameSpecialCharacters.split("");
    },
    whatsappFields() {
      return [
        {
          type: 0,
          fieldName: "metaAPIToken",
          displayName: "Meta API Token",
        },
        {
          type: 0,
          fieldName: "metaAccount"
        }
      ];
    },
    emailFields() {
      return [
        {
          type: 0,
          fieldName: "emailUser",
        },
        {
          input: "password",
          fieldName: "emailPassword",
        },
        {
          type: 0,
          fieldName: "emailSMTPServer",
          displayName: "Email SMTP Server",
        },
        {
          type: 0,
          fieldName: "emailSender",
        },
        {
          type: 0,
          fieldName: "emailSenderName",
        },
        {
          type: 2,
          fieldName: "emailPortNumber",
        },
        {
          type: 1,
          fieldName: "emailEnableSsl",
          displayName: "Email Enable SSL",
        },
      ];
    },
    workspaceFields() {
      return [
        {
          type: 0,
          fieldName: "name",
          displayName: "Workspace Name",
          required: true,
          validator: {
            format: designValidator.workspaceNameFormat,
          },
        },
      ];
    },
    fields() {
      return [
        {
          type: 0,
          fieldName: "companyName",
        },
        {
          type: 0,
          fieldName: "addressLine1",
          displayName: "Address Line 1",
        },
        {
          type: 0,
          fieldName: "addressLine2",
          displayName: "Address Line 2",
        },
        {
          type: 0,
          fieldName: "addressLine3",
          displayName: "Address Line 3",
        },
        {
          type: 0,
          fieldName: "postcode",
        },
        {
          type: 0,
          fieldName: "state",
        },
        {
          type: 27,
          fieldName: "country",
          optionList: countriesList,
          optionColor: false,
        },
        {
          type: 0,
          fieldName: "registrationNumber",
          displayName: "Business/Company Registration Number",
        },
        {
          type: 25,
          fieldName: "email",
        },
        {
          type: 26,
          fieldName: "telephoneNumber",
          country: this.formData && this.formData.country
        },
      ];
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    isFooter() {
      return (
        ["workspace", "company"].some((x) => x === this.section) &&
        !this.isError
      );
    }
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-workspace-modal {
  .workspace-modal__body {
    padding-top: 0 !important;
  }
  /* AI teammate instructions */
  .teammate-instructions-input {
    width: 100%;
    resize: vertical;
    padding: 8px 12px;
    border: 1px solid #d7dbe0;
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.5;

    &:focus {
      outline: none;
      border-color: #2b9ec6;
    }
  }

  /* SS03547 access tab */
  .access-option {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
    .access-option-label {
      margin-left: 0.5rem;
      cursor: pointer;
      user-select: none;
    }
  }
  .teammate-instructions-meta {
    display: flex;
    justify-content: space-between;
    margin: 4px 0 12px;
    font-size: 12px;
    color: #8a919c;
  }

  /* tabs */
  .user-profile-tabs {
    @include user-select-none;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    position: sticky;
    top: 0;
    margin: 0 -1rem 0.375rem;
    padding: 1rem;
    background-color: var(--fts-card-background);
    z-index: 2;
    .user-profile-tab {
      padding-bottom: 0.175rem;
      border-bottom: 2px solid transparent;
      font-weight: 500;
      transition: all 0.3s ease-in-out;
      cursor: pointer;
      text-transform: capitalize;
      &:not(:last-child) {
        margin-right: 1rem;
      }
      &:not(.active) {
        @include theme() {
          color: rgba(theme-get("fts-default"), 0.65);
        }
      }
      &:hover {
        @include theme() {
          color: rgba(theme-get("fts-default"), 0.85);
        }
      }
      &.active {
        border-color: var(--fts-default);
        color: var(--fts-default);
      }
    }
  }
  /* utilities */
  .workspace-modal-form-label {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--fts-text);
  }
  .workspace-modal-name-wrapper {
    margin-bottom: 1.5rem;
    .fts-form-group {
      margin-bottom: 1rem;
    }
  }
  /* plan */
  .plan-description {
    margin-bottom: 1rem;
  }
  .plan-wrapper {
    display: flex;
    align-items: flex-end;
    margin-bottom: 1rem;
    padding: 1rem;
    background-color: var(--fts-gray-100);
    border-radius: 0.575rem;
    .plan-info {
      flex: 1;
      margin-right: 1rem;
      .plan-name {
        margin-bottom: 0.175rem;
        font-size: 1.25rem;
        font-weight: 600;
      }
      .plan-price {
        display: flex;
        align-items: baseline;
        .plan-price-tag {
          margin-right: 0.375rem;
          font-size: 1.75rem;
          font-weight: 600;
          color: var(--fts-primary);
        }
        .plan-price-label {
          font-size: 1rem;
        }
      }
    }
  }
  /* workspace section */
  .workspace-section {
    padding-bottom: 1rem;
  }
  /* app section */
  .app-section {
    padding-bottom: 1rem;
    .app-store-btn {
      height: 40px;
      display: inline-flex;
      margin-top: 1.5rem;
      margin-bottom: 3rem;
      &:not(:first-child) {
        margin-left: 0.375rem;
      }
      > img {
        height: inherit;
      }
    }
  }
  /* error */
  .error-wrapper {
    padding: 1.5rem;
    text-align: center;
    .error-icon {
      height: 3rem;
      width: 3rem;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 1rem;
      border: 0.125rem solid var(--fts-danger);
      border-radius: 10em;
      font-size: 1.5rem;
      color: var(--fts-danger);
    }
    .error-title {
      margin-bottom: 0.125rem;
      font-weight: 500;
      font-size: 1.125rem;
      line-height: 1.75rem;
      color: var(--fts-danger);
    }
    .error-description {
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--fts-default);
    }
  }
  // preferences color
  .preferences-color-control {
    position: relative;
  }
  .preferences-color-toggle {
    @include absolute-center-y;
    right: 0.5rem;
    height: 1.25rem;
    width: 1.25rem;
    padding: 0;
    border: 0;
    background-color: transparent;
    cursor: pointer;
    &[type="color" i]::-webkit-color-swatch-wrapper {
      padding: 0;
    }
    &[type="color" i]::-webkit-color-swatch {
      border-radius: 10em;
      border-color: var(--fts-border);
    }
  }
  // collapsible element
  .collapsible-element-toggle {
    display: flex;
    color: var(--fts-text);
    cursor: pointer;
    &.collapsible-element-toggle--expand {
      .collapsible-element-toggle-action svg {
        transform: rotate(90deg);
      }
    }
  }
  .collapsible-element-toggle-header {
    flex: 1;
    margin-right: 1rem;
    .fts-form-label {
      cursor: inherit;
    }
    .fts-form-helper {
      margin-bottom: 0;
    }
  }
  .collapsible-element-toggle-action {
    height: 21px;
    width: 21px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: $comfort-border-radius;
    font-size: 0.875rem;
    svg {
      transition: transform 0.3s;
    }
    &:hover {
      background-color: rgba(var(--fts-gray-100-rgb), 0.75);
    }
  }
  .collapsible-element-content {
    margin-top: 0.75rem;
    padding: 1rem;
    border: 1px solid var(--fts-border);
    border-radius: $comfort-border-radius;
  }
  // setting
  .setting-wrapper {
    padding-top: 1.5rem;
    border-top: 1px solid var(--fts-border);
  }
  .setting-content {
    margin-top: 0.75rem;
  }
}
</style>